'use client';

import { createClient } from '@/lib/supabase/client';

export interface ProductReview {
  id: string;
  userId: string;
  productId: string;
  rating: number;
  title: string;
  reviewText: string;
  isVerifiedPurchase: boolean;
  status: 'pending' | 'approved' | 'rejected';
  helpfulCount: number;
  createdAt: string;
  updatedAt: string;
  reviewerName?: string;
}

export interface ConsultationReview {
  id: string;
  userId: string;
  consultationId: string;
  rating: number;
  title: string;
  reviewText: string;
  isVerifiedConsultation: boolean;
  status: 'pending' | 'approved' | 'rejected';
  helpfulCount: number;
  createdAt: string;
  updatedAt: string;
  reviewerName?: string;
}

export interface ReviewStats {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: { [key: number]: number };
  verifiedPurchasePercentage: number;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

export const reviewService = {
  // Product Reviews
  async getProductReviews(productId: string): Promise<ProductReview[]> {
    const supabase = createClient();
    
    try {
      const { data, error } = await supabase
        .from('product_reviews')
        .select(`
          *,
          user_profiles(first_name, last_name)
        `)
        .eq('product_id', productId)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        console.error('Data error:', error.message);
        return [];
      }

      return data?.map((row: any) => ({
        id: row.id,
        userId: row.user_id,
        productId: row.product_id,
        rating: row.rating,
        title: row.title,
        reviewText: row.review_text,
        isVerifiedPurchase: row.is_verified_purchase,
        status: row.status,
        helpfulCount: row.helpful_count,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        reviewerName: row.user_profiles ? `${row.user_profiles.first_name} ${row.user_profiles.last_name}` : 'Anonymous',
      })) || [];
    } catch (error: any) {
      if (error.code === 'PGRST116') {
        return [];
      }
      throw error;
    }
  },

  async createProductReview(review: Omit<ProductReview, 'id' | 'status' | 'helpfulCount' | 'createdAt' | 'updatedAt' | 'reviewerName'>): Promise<ProductReview | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      // Check if user has verified purchase
      const { data: skuData } = await supabase
        .from('skus')
        .select('name')
        .eq('id', review.productId)
        .single();

      const { data: verifiedData } = await supabase
        .rpc('has_verified_purchase', {
          p_user_id: user.id,
          p_product_name: skuData?.name || ''
        });

      const { data, error } = await supabase
        .from('product_reviews')
        .insert({
          user_id: review.userId,
          product_id: review.productId,
          rating: review.rating,
          title: review.title,
          review_text: review.reviewText,
          is_verified_purchase: verifiedData || false,
          status: 'pending',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        console.error('Data error:', error.message);
        return null;
      }

      return {
        id: data.id,
        userId: data.user_id,
        productId: data.product_id,
        rating: data.rating,
        title: data.title,
        reviewText: data.review_text,
        isVerifiedPurchase: data.is_verified_purchase,
        status: data.status,
        helpfulCount: data.helpful_count,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      throw error;
    }
  },

  async getProductReviewStats(productId: string): Promise<ReviewStats> {
    const supabase = createClient();
    
    try {
      const { data, error } = await supabase
        .from('product_reviews')
        .select('rating, is_verified_purchase')
        .eq('product_id', productId)
        .eq('status', 'approved');

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        console.error('Data error:', error.message);
        return {
          averageRating: 0,
          totalReviews: 0,
          ratingDistribution: {},
          verifiedPurchasePercentage: 0,
        };
      }

      if (!data || data.length === 0) {
        return {
          averageRating: 0,
          totalReviews: 0,
          ratingDistribution: {},
          verifiedPurchasePercentage: 0,
        };
      }

      const totalReviews = data.length;
      const sumRatings = data.reduce((sum, review) => sum + review.rating, 0);
      const averageRating = sumRatings / totalReviews;

      const ratingDistribution: { [key: number]: number } = {};
      for (let i = 1; i <= 5; i++) {
        ratingDistribution[i] = data.filter(r => r.rating === i).length;
      }

      const verifiedCount = data.filter(r => r.is_verified_purchase).length;
      const verifiedPurchasePercentage = (verifiedCount / totalReviews) * 100;

      return {
        averageRating: Math.round(averageRating * 10) / 10,
        totalReviews,
        ratingDistribution,
        verifiedPurchasePercentage: Math.round(verifiedPurchasePercentage),
      };
    } catch (error: any) {
      if (error.code === 'PGRST116') {
        return {
          averageRating: 0,
          totalReviews: 0,
          ratingDistribution: {},
          verifiedPurchasePercentage: 0,
        };
      }
      throw error;
    }
  },

  // Consultation Reviews
  async getConsultationReviews(consultationId: string): Promise<ConsultationReview[]> {
    const supabase = createClient();
    
    try {
      const { data, error } = await supabase
        .from('consultation_reviews')
        .select(`
          *,
          user_profiles(first_name, last_name)
        `)
        .eq('consultation_id', consultationId)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        console.error('Data error:', error.message);
        return [];
      }

      return data?.map((row: any) => ({
        id: row.id,
        userId: row.user_id,
        consultationId: row.consultation_id,
        rating: row.rating,
        title: row.title,
        reviewText: row.review_text,
        isVerifiedConsultation: row.is_verified_consultation,
        status: row.status,
        helpfulCount: row.helpful_count,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        reviewerName: row.user_profiles ? `${row.user_profiles.first_name} ${row.user_profiles.last_name}` : 'Anonymous',
      })) || [];
    } catch (error: any) {
      if (error.code === 'PGRST116') {
        return [];
      }
      throw error;
    }
  },

  async createConsultationReview(review: Omit<ConsultationReview, 'id' | 'status' | 'helpfulCount' | 'createdAt' | 'updatedAt' | 'reviewerName'>): Promise<ConsultationReview | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      // Check if user has completed consultation
      const { data: verifiedData } = await supabase
        .rpc('has_completed_consultation', {
          p_user_id: user.id,
          p_consultation_id: review.consultationId
        });

      const { data, error } = await supabase
        .from('consultation_reviews')
        .insert({
          user_id: review.userId,
          consultation_id: review.consultationId,
          rating: review.rating,
          title: review.title,
          review_text: review.reviewText,
          is_verified_consultation: verifiedData || false,
          status: 'pending',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        console.error('Data error:', error.message);
        return null;
      }

      return {
        id: data.id,
        userId: data.user_id,
        consultationId: data.consultation_id,
        rating: data.rating,
        title: data.title,
        reviewText: data.review_text,
        isVerifiedConsultation: data.is_verified_consultation,
        status: data.status,
        helpfulCount: data.helpful_count,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      throw error;
    }
  },

  // Admin: Get all pending reviews
  async getPendingReviews(): Promise<{ productReviews: ProductReview[]; consultationReviews: ConsultationReview[] }> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const [productResult, consultationResult] = await Promise.all([
        supabase
          .from('product_reviews')
          .select(`
            *,
            user_profiles(first_name, last_name),
            skus(name)
          `)
          .eq('status', 'pending')
          .order('created_at', { ascending: false }),
        supabase
          .from('consultation_reviews')
          .select(`
            *,
            user_profiles(first_name, last_name),
            consultations(provider_name)
          `)
          .eq('status', 'pending')
          .order('created_at', { ascending: false }),
      ]);

      const productReviews = productResult.data?.map((row: any) => ({
        id: row.id,
        userId: row.user_id,
        productId: row.product_id,
        rating: row.rating,
        title: row.title,
        reviewText: row.review_text,
        isVerifiedPurchase: row.is_verified_purchase,
        status: row.status,
        helpfulCount: row.helpful_count,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        reviewerName: row.user_profiles ? `${row.user_profiles.first_name} ${row.user_profiles.last_name}` : 'Anonymous',
        productName: row.skus?.name,
      })) || [];

      const consultationReviews = consultationResult.data?.map((row: any) => ({
        id: row.id,
        userId: row.user_id,
        consultationId: row.consultation_id,
        rating: row.rating,
        title: row.title,
        reviewText: row.review_text,
        isVerifiedConsultation: row.is_verified_consultation,
        status: row.status,
        helpfulCount: row.helpful_count,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        reviewerName: row.user_profiles ? `${row.user_profiles.first_name} ${row.user_profiles.last_name}` : 'Anonymous',
        providerName: row.consultations?.provider_name,
      })) || [];

      return { productReviews, consultationReviews };
    } catch (error: any) {
      if (error.code === 'PGRST116') {
        return { productReviews: [], consultationReviews: [] };
      }
      throw error;
    }
  },

  // Admin: Moderate review
  async moderateReview(reviewId: string, reviewType: 'product' | 'consultation', status: 'approved' | 'rejected'): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const tableName = reviewType === 'product' ? 'product_reviews' : 'consultation_reviews';

    const { error } = await supabase
      .from(tableName)
      .update({ status })
      .eq('id', reviewId);

    if (error) {
      if (isSchemaError(error)) {
        throw error;
      }
      console.error('Moderation error:', error.message);
    }
  },

  // Mark review as helpful
  async markReviewHelpful(reviewId: string, reviewType: 'product' | 'consultation'): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('review_helpful_votes')
      .insert({
        user_id: user.id,
        review_type: reviewType,
        review_id: reviewId,
      });

    if (error) {
      if (isSchemaError(error)) {
        throw error;
      }
      console.error('Helpful vote error:', error.message);
    }
  },
};